import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export interface AppInsightsRuntimeConfig {
  enabled?: boolean;
  connectionString?: string;
}

export interface RuntimeConfig {
  appInsights?: AppInsightsRuntimeConfig;
}

@Injectable({
  providedIn: 'root'
})
/**
 * Loads optional runtime configuration from assets/config/runtime-config.local.json and
 * merges it over compile-time environment telemetry defaults. This enables per-
 * environment overrides (for example, App Insights connection strings injected
 * during deployment) without rebuilding the Angular bundle.
 */
export class RuntimeConfigService {
  private readonly defaultAppInsightsConfig = this.extractDefaultAppInsightsConfig();
  private loaded = false;

  private config: RuntimeConfig = {
    appInsights: {
      enabled: this.defaultAppInsightsConfig.enabled,
      connectionString: this.defaultAppInsightsConfig.connectionString
    }
  };

  async load(): Promise<void> {
    try {
      const response = await fetch('assets/config/runtime-config.local.json', {
        cache: 'no-store' // Avoid stale cached config after deployments.
      });

      if (!response.ok) {
        return; // Preserve existing config if runtime config is not available.
      }

      // Merge the runtime configuration over the existing configuration, giving precedence to runtime values.
      const runtimeConfig = (await response.json()) as RuntimeConfig;
      this.config = {
        ...this.config,
        ...runtimeConfig,
        appInsights: {
          ...this.config.appInsights,
          ...runtimeConfig.appInsights
        }
      };
    } catch {
      // Fail-safe by design. Telemetry configuration is optional.
    } finally {
      this.loaded = true;
    }
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getAppInsightsConfig(): AppInsightsRuntimeConfig {
    return this.config.appInsights ?? {};
  }

  private extractDefaultAppInsightsConfig(): AppInsightsRuntimeConfig {
    const runtimeEnvironment = environment as unknown as {
      telemetry?: {
        appInsights?: AppInsightsRuntimeConfig;
      };
    };

    return {
      enabled: runtimeEnvironment.telemetry?.appInsights?.enabled ?? false,
      connectionString: runtimeEnvironment.telemetry?.appInsights?.connectionString ?? ''
    };
  }
}
